/// <amd-module name="Wit/DirectoryView/Components/DirectoryView" />
import * as React from "react";
import { TeamProfilePanelIconButton } from "TFS/TeamProfilePanelIconButton/TeamProfilePanelIconButton";
import { ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { onClickFPS } from "VSS/Platform/FPS";
import { VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { ContentSize } from "VSSUI/Callout";
import { Card } from "VSSUI/Card";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { Header, TitleSize } from "VSSUI/Header";
import { getFilterItem, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Icon } from "VSSUI/Icon";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Panel } from "VSSUI/Panel";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { SimpleTableCell } from "VSSUI/Table";
import { ExpandableTreeCell, ITreeColumn, ITreeRow, Tree } from "VSSUI/TreeEx";
import { ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";
import { ZeroData } from "VSSUI/ZeroData";
import { DirectoryItem } from "Wit/DirectoryService/Client/Contracts/Directory";
import { GroupIds, IDirectoryService } from "Wit/DirectoryService/IDirectoryService";
import { IDirectoryTreeItem } from "Wit/DirectoryService/Models";
import { DirectoryFavoriteButton } from "Wit/DirectoryView/Components/DirectoryFavoriteButton";
import { DirectoryFilterBar } from "Wit/DirectoryView/Components/DirectoryFilterBar";
import * as Resources from "Wit/DirectoryView/Resources";

export interface IDirectoryViewProps<T extends DirectoryItem = DirectoryItem> {
    /**
     * Additional columns to display in the tree
     */
    additionalColumns?: ITreeColumn<IDirectoryTreeItem<T>>[];
    /**
     * Optional header item to be added next to the filter button
     */
    additionalHeaderItem?: IHeaderCommandBarItem;
    /**
     * Favorite artifact identifier
     */
    artifactType: string;
    /**
     * Name of the directory item type, e.g. "boards"
     */
    directoryItemName: string;
    /**
     * Header text at the top of the directory view
     */
    pageTitle: string;
    /**
     * Name of the icon to show in front of directory items
     */
    rowIconName: string;
    /**
     * Some implementation of the IDirectoryService interface
     */
    directoryService: IDirectoryService<T>;
    /**
     * Aria label for the directory tree
     */
    treeAriaLabel?: string;
    /**
     * Id of the zero data contribution
     */
    zeroDataContributionId: string;
}

const GroupResourceStrings: { [key: string]: string } = {
    [GroupIds.All]: Resources.AllTeamsLabel,
    [GroupIds.Favorites]: Resources.FavoriteTeamsLabel,
    [GroupIds.Recent]: Resources.RecentItemsLabel,
    [GroupIds.Team]: Resources.MyTeamItemsLabel
};

export class DirectoryView<T extends DirectoryItem = DirectoryItem> extends VssComponent<IDirectoryViewProps<T>> {
    private filterVisible = new ObservableValue<boolean>(false);

    public render(): JSX.Element {
        const { additionalColumns, directoryService, pageTitle, treeAriaLabel, zeroDataContributionId } = this.props;
        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="backlogs-page flex-grow flex-column">
                    <Observer isFiltering={directoryService.isFiltering}>
                        {() => {
                            return <Header title={pageTitle} titleSize={TitleSize.Large} commandBarItems={this.commandBarItems()} />;
                        }}
                    </Observer>
                    <div className="page-content page-content-top rhythm-vertical-16">
                        <ConditionalChildren renderChildren={this.filterVisible}>
                            <DirectoryFilterBar directoryService={directoryService} />
                        </ConditionalChildren>
                        <Observer treeItems={directoryService.getDirectoryTreeItemProvider()}>
                            {(props: { treeItems: ITreeItemEx<IDirectoryTreeItem<T>>[] }) => {
                                const classes = props.treeItems.length > 0 ? "flex-grow bolt-table-card bolt-backlogs-card" : "flex-center";
                                return (
                                    <Card className={classes} contentProps={{ contentPadding: false }}>
                                        {props.treeItems.length > 0 ? (
                                            <Tree
                                                ariaLabel={treeAriaLabel}
                                                columns={additionalColumns ? [...this.defaultColumns, ...additionalColumns] : this.defaultColumns}
                                                itemProvider={directoryService.getDirectoryTreeItemProvider()}
                                                onActivate={this.onActivate}
                                                onToggle={(event, treeItem: ITreeItemEx<IDirectoryTreeItem<T>>) => {
                                                    directoryService.toggleTreeItem(treeItem);
                                                }}
                                                scrollable={false}
                                            />
                                        ) : (
                                            <ZeroData
                                                imagePath={getAssetLocation(this.context.pageContext, zeroDataContributionId)}
                                                imageAltText={Resources.ZeroDataPrimaryText}
                                                primaryText={Resources.ZeroDataPrimaryText}
                                                secondaryText={Resources.ZeroDataSecondaryText}
                                            />
                                        )}
                                    </Card>
                                );
                            }}
                        </Observer>
                    </div>
                </Page>
            </Surface>
        );
    }

    private onActivate = (event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>, treeRow: ITreeRow<IDirectoryTreeItem<T>>) => {
        const treeItem = treeRow.data.underlyingItem.data;

        if (treeItem?.directoryItem.url) {
            const pageContext = this.context.pageContext;
            onClickFPS(pageContext, treeItem.directoryItem.url, true, event);
        } else if (treeItem.isFolder) {
            this.props.directoryService.toggleTreeItem(treeRow.data);
        }
    };

    private renderNameCell = (
        rowIndex: number,
        columnIndex: number,
        treeColumn: ITreeColumn<IDirectoryTreeItem<T>>,
        treeItem: ITreeItemEx<IDirectoryTreeItem<T>>
    ): JSX.Element => {
        const key = `name-${rowIndex}-${columnIndex}`;
        const item = treeItem.underlyingItem.data;
        const directoryItem = item.directoryItem;
        if (!item.isFolder) {
            return (
                <ExpandableTreeCell contentClassName="bolt-tree-cell-content-with-button" columnIndex={columnIndex} key={key} treeItem={treeItem}>
                    <Icon className="icon-margin" iconName={this.props.rowIconName} />
                    {`${directoryItem.name} ${this.props.directoryItemName}`}
                    <div className="flex-grow" />
                    <DirectoryFavoriteButton
                        artifactType={this.props.artifactType}
                        className="bolt-table-cell-content-reveal directory-favorite-cell"
                        item={directoryItem}
                        onAddFavorite={() => this.props.directoryService.addFavorite(directoryItem.id)}
                        onRemoveFavorite={() => this.props.directoryService.removeFavorite(directoryItem.id)}
                    />
                </ExpandableTreeCell>
            );
        }

        const resourceString = GroupResourceStrings[directoryItem.id];
        const text = resourceString ? format(resourceString, this.props.directoryItemName) : "";
        return (
            <ExpandableTreeCell
                contentClassName="font-weight-semibold"
                columnIndex={columnIndex}
                key={key}
                treeColumn={treeColumn}
                treeItem={treeItem}
            >
                {text}
            </ExpandableTreeCell>
        );
    };

    private renderTeamCell = (
        rowIndex: number,
        columnIndex: number,
        treeColumn: ITreeColumn<IDirectoryTreeItem<T>>,
        treeItem: ITreeItemEx<IDirectoryTreeItem<T>>
    ): JSX.Element => {
        const item = treeItem.underlyingItem.data;
        const directoryItem = item.directoryItem;
        return (
            <SimpleTableCell
                ariaRowIndex={rowIndex}
                contentClassName="bolt-table-cell-content-with-button"
                columnIndex={columnIndex}
                key={`team-${rowIndex}-${columnIndex}`}
            >
                {!item.isFolder && (
                    <>
                        <div className="margin-right-4">
                            <TeamProfilePanelIconButton
                                renderLoadingPanel={(onDismiss: () => void) => (
                                    <Panel onDismiss={onDismiss} size={ContentSize.Medium}>
                                        <div className="flex-column flex-center flex-grow justify-center">
                                            <Spinner size={SpinnerSize.large} />
                                        </div>
                                    </Panel>
                                )}
                                teamId={directoryItem.id}
                                teamName={directoryItem.name}
                            />
                        </div>
                        {directoryItem.name}
                    </>
                )}
            </SimpleTableCell>
        );
    };

    private commandBarItems = (): IHeaderCommandBarItem[] => {
        const toggleFilter = (): void => {
            this.filterVisible.value = !this.filterVisible.value;
        };
        const isFiltering = this.props.directoryService.isFiltering.value;
        const filterItem = getFilterItem(toggleFilter, isFiltering, /* important */ true, Resources.ShowFilter);

        if (this.props.additionalHeaderItem) {
            return [filterItem, this.props.additionalHeaderItem];
        } else {
            return [filterItem];
        }
    };

    private onSize = (event: MouseEvent, index: number, width: number, column: ITreeColumn<IDirectoryTreeItem<T>>) => {
        (column.width as ObservableValue<number>).value = width;
    };

    private defaultColumns: ITreeColumn<IDirectoryTreeItem<T>>[] = [
        { id: "name", name: Resources.NameLabel, width: new ObservableValue(-100), renderCell: this.renderNameCell, onSize: this.onSize },
        { id: "team", name: Resources.TeamLabel, width: new ObservableValue(-70), renderCell: this.renderTeamCell, onSize: this.onSize }
    ];
}
